import React, { useState } from "react";
import { FiAlertTriangle } from "react-icons/fi";
import { Modal } from "newui/components/ui/Modal";
import { Button } from "newui/components/Button";
import { TextAreaWithLabel } from "newui/components/ui/InputWithLabel/TextAreaWithLabel";
import type { CriteriaListItem } from "../../types/meeting.type";

interface IncompleteCompletionModalProps {
  incompleteCriteria: CriteriaListItem[];
  isOpen: boolean;
  isSubmitting: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}

export const IncompleteCompletionModal: React.FC<IncompleteCompletionModalProps> = ({
  isOpen,
  incompleteCriteria,
  isSubmitting,
  onClose,
  onConfirm,
}) => {
  const [reason, setReason] = useState("");

  const handleClose = () => {
    if (!isSubmitting) {
      setReason("");
      onClose();
    }
  };

  const handleConfirm = () => {
    onConfirm(reason);
  };

  const canProceed = reason.trim().length >= 10;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Complete Meeting with Incomplete Scores?"
      titleSize="xl"
      size="md"
      modalType="alert"
      iconType="warning"
      closeOnOverlayClick={!isSubmitting}
      closeOnEsc={!isSubmitting}
      customFooter={
        <div className="flex gap-3 justify-end w-full">
          <Button
            btntype="secondary"
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            btntype="primary"
            type="button"
            onClick={handleConfirm}
            disabled={!canProceed || isSubmitting}
            isLoading={isSubmitting}
            data-testid="incomplete-confirm-btn"
          >
            Complete Anyway
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-700">
          Not all criteria have consensus scores. Completing the meeting now will
          finalise results with missing scores. This action is recorded for audit
          purposes.
        </p>

        <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
          <div className="flex items-center gap-2 mb-2">
            <FiAlertTriangle className="text-amber-600 shrink-0" size={14} />
            <span className="text-xs font-semibold text-amber-700">
              {incompleteCriteria.length} incomplete{" "}
              {incompleteCriteria.length === 1 ? "criterion" : "criteria"}
            </span>
          </div>
          <ul className="space-y-1 pl-5">
            {incompleteCriteria.map((c) => (
              <li
                key={c.id}
                className="text-xs text-amber-800 list-disc"
              >
                {c.name}
              </li>
            ))}
          </ul>
        </div>

        <TextAreaWithLabel
          label="Reason for completing without full scores"
          required
          placeholder="Enter a reason (minimum 10 characters)"
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={isSubmitting}
          testId="incomplete-reason-input"
          hasError={reason.length > 0 && reason.trim().length < 10}
          errorText={
            reason.length > 0 && reason.trim().length < 10
              ? "Reason must be at least 10 characters."
              : undefined
          }
          showCharacterCount={false}
          resize="vertical"
        />
      </div>
    </Modal>
  );
};
